import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {envIsBeta, envIsProd} from '../../config';
import {recordRumError} from '../../util/datadog/datadogRum';
import {ignoredErrorMessages} from '../../util/ignoredErrorMessages';

export interface CrashReportError {
  /** The console message that identified the crash, reported alongside the comment. */
  message: string;
}

/**
 * Holds the crash currently being reported, if any.
 *
 * Crashes are detected outside React, so the trigger has to live outside the
 * component tree. Store state exists before `CrashReportDialog` mounts, so a
 * crash during initial load opens the dialog as soon as it renders.
 */
export type CrashReportDialogStore = {
  error: CrashReportError | null;
  show: (error: CrashReportError) => void;
  close: () => void;
};

export const useCrashReportDialogStore = create<CrashReportDialogStore>()(
  devtools(
    set => ({
      error: null,
      show: (error: CrashReportError) => set({error}, false, 'show'),
      close: () => set({error: null}, false, 'close'),
    }),
    {name: 'CrashReportDialogStore'}
  )
);

let lastReportedMessage: string | null = null;

/**
 * Opens the crash report dialog from outside React, and reports that it was
 * shown.
 *
 * Known-benign messages and a repeat of the crash just reported are skipped, so
 * a crash that keeps firing doesn't keep reopening the dialog.
 */
export function showCrashReportDialog(error: CrashReportError): void {
  if (!envIsProd && !envIsBeta) {
    return;
  }
  if (ignoredErrorMessages.some(ignored => error.message.includes(ignored))) {
    return;
  }
  if (error.message === lastReportedMessage) {
    return;
  }
  lastReportedMessage = error.message;

  recordRumError(error.message, {
    message: 'crash_report_dialog_shown',
  });
  useCrashReportDialogStore.getState().show(error);
}
